import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Box, TextField, Button, Typography, Alert, Container, IconButton, 
  InputAdornment, CircularProgress, Fade, Zoom, Slide,
  alpha
} from '@mui/material';
import { 
  Language, Brightness4, Brightness7, Visibility, VisibilityOff,
  Person, Lock, Login as LoginIcon, AccountBalance,
  TrendingUp, Security, Speed
} from '@mui/icons-material';
import authService from '../../services/auth.service';
import { useAppDispatch, useAppSelector } from '../../hooks/redux.hooks';
import { setCredentials } from '../../store/slices/authSlice';
import { requestNotificationPermission, showLoginNotification } from '../../utils/appNotifications';
import { useLanguage } from '../../contexts/LanguageContext';
import { useTheme } from '../../contexts/ThemeContext';

const translations = {
  en: {
    title: 'Countora',
    subtitle: 'Advanced Accounting & Inventory System',
    username: 'Username',
    password: 'Password',
    login: 'Sign In',
    loggingIn: 'Signing in...',
    defaultCred: 'Demo: admin / Admin@123',
    loginFailed: 'Login failed. Please check your credentials.',
    welcome: 'Welcome Back!',
    signInContinue: 'Sign in to continue to your dashboard',
    feature1: 'Real-time Analytics',
    feature2: 'Secure & Reliable',
    feature3: 'Fast Performance',
    rememberMe: 'Remember me',
    forgotPassword: 'Forgot password?',
    copyright: '© 2024 Countora. All rights reserved.'
  },
  ar: {
    title: 'كونتورا',
    subtitle: 'نظام محاسبة وجرد متقدم',
    username: 'اسم المستخدم',
    password: 'كلمة المرور',
    login: 'تسجيل الدخول',
    loggingIn: 'جاري الدخول...',
    defaultCred: 'تجريبي: admin / Admin@123',
    loginFailed: 'فشل تسجيل الدخول. تحقق من بياناتك.',
    welcome: 'مرحباً بعودتك!',
    signInContinue: 'سجل دخولك للمتابعة إلى لوحة التحكم',
    feature1: 'تحليلات فورية',
    feature2: 'آمن وموثوق',
    feature3: 'أداء سريع',
    rememberMe: 'تذكرني',
    forgotPassword: 'نسيت كلمة المرور؟',
    copyright: '© 2024 كونتورا. جميع الحقوق محفوظة.'
  },
};

// Floating shapes component
const FloatingShapes: React.FC<{ isDark: boolean }> = ({ isDark }) => (
  <Box sx={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
    {/* Large circle */}
    <Box sx={{
      position: 'absolute',
      width: { xs: 300, md: 500 },
      height: { xs: 300, md: 500 },
      borderRadius: '50%',
      background: isDark 
        ? 'radial-gradient(circle, rgba(144,202,249,0.1) 0%, transparent 70%)'
        : 'radial-gradient(circle, rgba(255,255,255,0.15) 0%, transparent 70%)',
      top: '-10%',
      right: '-10%',
      animation: 'float 20s ease-in-out infinite',
      '@keyframes float': {
        '0%, 100%': { transform: 'translate(0, 0) rotate(0deg)' },
        '25%': { transform: 'translate(-20px, 20px) rotate(5deg)' },
        '50%': { transform: 'translate(0, 40px) rotate(0deg)' },
        '75%': { transform: 'translate(20px, 20px) rotate(-5deg)' },
      }
    }} />
    
    {/* Medium circle */}
    <Box sx={{
      position: 'absolute',
      width: { xs: 200, md: 350 },
      height: { xs: 200, md: 350 },
      borderRadius: '50%',
      background: isDark 
        ? 'radial-gradient(circle, rgba(206,147,216,0.1) 0%, transparent 70%)'
        : 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
      bottom: '-5%',
      left: '-5%',
      animation: 'float2 25s ease-in-out infinite',
      '@keyframes float2': {
        '0%, 100%': { transform: 'translate(0, 0) scale(1)' },
        '50%': { transform: 'translate(30px, -30px) scale(1.05)' },
      }
    }} />

    {/* Small decorative circles */}
    {[...Array(6)].map((_, i) => (
      <Box key={i} sx={{
        position: 'absolute',
        width: { xs: 8, md: 12 },
        height: { xs: 8, md: 12 },
        borderRadius: '50%',
        bgcolor: 'rgba(255,255,255,0.3)',
        top: `${15 + i * 15}%`,
        left: `${10 + i * 12}%`,
        animation: `pulse${i} ${3 + i * 0.5}s ease-in-out infinite`,
        [`@keyframes pulse${i}`]: {
          '0%, 100%': { opacity: 0.3, transform: 'scale(1)' },
          '50%': { opacity: 0.6, transform: 'scale(1.5)' },
        }
      }} />
    ))}

    {/* Gradient overlay lines */}
    <Box sx={{
      position: 'absolute',
      inset: 0,
      background: `repeating-linear-gradient(
        45deg,
        transparent,
        transparent 100px,
        ${isDark ? 'rgba(255,255,255,0.02)' : 'rgba(255,255,255,0.03)'} 100px,
        ${isDark ? 'rgba(255,255,255,0.02)' : 'rgba(255,255,255,0.03)'} 200px
      )`,
    }} />
  </Box>
);

// Feature item component
const FeatureItem: React.FC<{ icon: React.ReactNode; text: string; delay: number }> = ({ icon, text, delay }) => (
  <Fade in timeout={1000} style={{ transitionDelay: `${delay}ms` }}>
    <Box sx={{ 
      display: 'flex', 
      alignItems: 'center', 
      gap: 1.5,
      color: 'rgba(255,255,255,0.9)',
      py: 1
    }}>
      <Box sx={{ 
        p: 1, 
        borderRadius: 2, 
        bgcolor: 'rgba(255,255,255,0.15)',
        display: 'flex',
        backdropFilter: 'blur(10px)'
      }}>
        {icon}
      </Box>
      <Typography variant="body2" fontWeight="500">{text}</Typography>
    </Box>
  </Fade>
);

const LoginPage: React.FC = () => {
  const [userName, setUserName] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [mounted, setMounted] = useState(false);
  
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const existingToken = useAppSelector((state) => state.auth.token);
  const { language, toggleLanguage } = useLanguage();
  const { mode, toggleTheme } = useTheme();
  const t = translations[language];
  const isRTL = language === 'ar';
  const isDark = mode === 'dark';

  useEffect(() => {
    setMounted(true);
    // If already logged in (e.g. authenticated in another tab), skip the login form
    if (isAuthenticated && existingToken) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, existingToken, navigate]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await authService.login({ userName, password });
      dispatch(setCredentials(response));
      // Request permission first (no-op if already granted/denied), then notify
      await requestNotificationPermission();
      showLoginNotification(response.fullName ?? response.userName ?? 'User');
      navigate('/dashboard');
    } catch (err: any) {
      const serverMsg = err.response?.data?.message;
      setError(serverMsg || t.loginFailed);
    } finally {
      setLoading(false);
    }
  };

  // Dynamic colors based on theme
  const colors = {
    bgGradient: isDark 
      ? 'linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%)'
      : 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
    cardBg: isDark 
      ? alpha('#1a1a2e', 0.85)
      : alpha('#ffffff', 0.95),
    inputBg: isDark ? '#2d2d44' : '#f8f9ff',
    primary: isDark ? '#90caf9' : '#667eea',
    secondary: isDark ? '#ce93d8' : '#764ba2',
    text: isDark ? '#ffffff' : '#1a1a2e',
    textSecondary: isDark ? '#b0b0b0' : '#666666',
  };

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      display: 'flex',
      position: 'relative',
      overflow: 'hidden',
      background: colors.bgGradient,
      direction: isRTL ? 'rtl' : 'ltr',
    }}>
      {/* Floating Background Shapes */}
      <FloatingShapes isDark={isDark} />

      {/* Theme & Language Toggle */}
      <Box sx={{ 
        position: 'fixed', 
        top: 20, 
        [isRTL ? 'left' : 'right']: 20,
        display: 'flex', 
        gap: 1,
        zIndex: 10,
        flexDirection: isRTL ? 'row-reverse' : 'row',
      }}>
        <Zoom in={mounted} style={{ transitionDelay: '200ms' }}>
          <IconButton 
            onClick={toggleLanguage} 
            sx={{ 
              bgcolor: 'rgba(255,255,255,0.15)', 
              color: 'white',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255,255,255,0.2)',
              '&:hover': { 
                bgcolor: 'rgba(255,255,255,0.25)',
                transform: 'scale(1.1)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            <Language />
          </IconButton>
        </Zoom>
        <Zoom in={mounted} style={{ transitionDelay: '300ms' }}>
          <IconButton 
            onClick={toggleTheme} 
            sx={{ 
              bgcolor: 'rgba(255,255,255,0.15)', 
              color: 'white',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255,255,255,0.2)',
              '&:hover': { 
                bgcolor: 'rgba(255,255,255,0.25)',
                transform: 'rotate(180deg)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            {isDark ? <Brightness7 /> : <Brightness4 />}
          </IconButton>
        </Zoom>
      </Box>

      <Container maxWidth="lg" sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        minHeight: '100vh',
        py: 4
      }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: 'center',
          gap: { xs: 4, md: 8 },
          width: '100%',
          maxWidth: 1000
        }}>
          
          {/* Left Side - Branding */}
          <Slide direction={isRTL ? "left" : "right"} in={mounted} timeout={800}>
            <Box sx={{ 
              flex: 1, 
              color: 'white',
              textAlign: { xs: 'center', md: isRTL ? 'right' : 'left' },
              display: { xs: 'none', md: 'block' }
            }}>
              {/* Logo */}
              <Box sx={{ 
                display: 'inline-flex',
                alignItems: 'center',
                gap: 2,
                mb: 4
              }}>
                <Box sx={{ 
                  width: 70, 
                  height: 70, 
                  borderRadius: 3,
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0.1) 100%)',
                  backdropFilter: 'blur(20px)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '1px solid rgba(255,255,255,0.3)',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
                }}>
                  <AccountBalance sx={{ fontSize: 40 }} />
                </Box>
                <Box>
                  <Typography variant="h3" fontWeight="800" sx={{ letterSpacing: '-1px' }}>
                    {t.title}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8, mt: -0.5 }}>
                    {t.subtitle}
                  </Typography>
                </Box>
              </Box>

              {/* Welcome Text */}
              <Typography variant="h4" fontWeight="bold" sx={{ mb: 1 }}>
                {t.welcome}
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.85, mb: 4 }}>
                {t.signInContinue}
              </Typography>

              {/* Features */}
              <Box sx={{ mt: 4 }}>
                <FeatureItem 
                  icon={<TrendingUp sx={{ fontSize: 22 }} />} 
                  text={t.feature1} 
                  delay={400}
                />
                <FeatureItem 
                  icon={<Security sx={{ fontSize: 22 }} />} 
                  text={t.feature2} 
                  delay={600}
                />
                <FeatureItem 
                  icon={<Speed sx={{ fontSize: 22 }} />} 
                  text={t.feature3} 
                  delay={800}
                />
              </Box>
            </Box>
          </Slide>

          {/* Right Side - Login Form */}
          <Zoom in={mounted} timeout={600}>
            <Box sx={{ 
              width: { xs: '100%', sm: 420 },
              position: 'relative'
            }}>
              {/* Glow effect behind card */}
              <Box sx={{
                position: 'absolute',
                inset: -2,
                borderRadius: 5,
                background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`,
                filter: 'blur(20px)',
                opacity: 0.5,
                zIndex: 0
              }} />

              {/* Login Card */}
              <Box sx={{ 
                position: 'relative',
                zIndex: 1,
                bgcolor: colors.cardBg,
                borderRadius: 4,
                backdropFilter: 'blur(20px)',
                border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.5)'}`,
                boxShadow: isDark 
                  ? '0 25px 50px rgba(0,0,0,0.5)'
                  : '0 25px 50px rgba(102, 126, 234, 0.3)',
                overflow: 'hidden'
              }}>
                {/* Card Header */}
                <Box sx={{ 
                  background: isDark 
                    ? 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)'
                    : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  p: 4,
                  textAlign: 'center',
                  position: 'relative',
                  overflow: 'hidden'
                }}>
                  {/* Header decorations */}
                  <Box sx={{ 
                    position: 'absolute', 
                    top: -30, 
                    right: -30, 
                    width: 100, 
                    height: 100, 
                    borderRadius: '50%', 
                    bgcolor: 'rgba(255,255,255,0.1)' 
                  }} />
                  <Box sx={{ 
                    position: 'absolute', 
                    bottom: -20, 
                    left: -20, 
                    width: 60, 
                    height: 60, 
                    borderRadius: '50%', 
                    bgcolor: 'rgba(255,255,255,0.08)' 
                  }} />

                  {/* Mobile Logo */}
                  <Box sx={{ display: { xs: 'block', md: 'none' }, mb: 2 }}>
                    <Box sx={{ 
                      width: 60, 
                      height: 60, 
                      borderRadius: 3,
                      background: 'rgba(255,255,255,0.2)',
                      backdropFilter: 'blur(10px)',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 2
                    }}>
                      <AccountBalance sx={{ fontSize: 32, color: 'white' }} />
                    </Box>
                    <Typography variant="h4" fontWeight="800" color="white">
                      {t.title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)' }}>
                      {t.subtitle}
                    </Typography>
                  </Box>

                  {/* Desktop Header */}
                  <Box sx={{ display: { xs: 'none', md: 'block' }, position: 'relative', zIndex: 1 }}>
                    <Box sx={{ 
                      width: 64, 
                      height: 64, 
                      borderRadius: 3,
                      background: 'rgba(255,255,255,0.2)',
                      backdropFilter: 'blur(10px)',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 2,
                      border: '2px solid rgba(255,255,255,0.3)'
                    }}>
                      <LoginIcon sx={{ fontSize: 32, color: 'white' }} />
                    </Box>
                    <Typography variant="h5" fontWeight="bold" color="white">
                      {t.welcome}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', mt: 0.5 }}>
                      {t.signInContinue}
                    </Typography>
                  </Box>
                </Box>

                {/* Form Content */}
                <Box sx={{ p: 4 }}>
                  {error && (
                    <Fade in>
                      <Alert 
                        severity="error" 
                        sx={{ 
                          mb: 3, 
                          borderRadius: 2,
                          bgcolor: isDark ? alpha('#f44336', 0.15) : alpha('#f44336', 0.1),
                          color: isDark ? '#ff8a80' : '#d32f2f'
                        }}
                        onClose={() => setError('')}
                      >
                        {error}
                      </Alert>
                    </Fade>
                  )}

                  <form onSubmit={handleLogin}>
                    {/* Username Field */}
                    <TextField 
                      label={t.username}
                      fullWidth 
                      value={userName} 
                      onChange={(e) => setUserName(e.target.value)} 
                      required 
                      autoFocus
                      sx={{ 
                        mb: 3,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          bgcolor: colors.inputBg,
                          '&:hover fieldset': { borderColor: colors.primary },
                          '&.Mui-focused fieldset': { borderColor: colors.primary, borderWidth: 2 }
                        },
                        '& .MuiInputLabel-root.Mui-focused': { color: colors.primary }
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Person sx={{ color: colors.textSecondary }} />
                          </InputAdornment>
                        ),
                      }}
                    />

                    {/* Password Field */}
                    <TextField 
                      label={t.password}
                      type={showPassword ? 'text' : 'password'}
                      fullWidth 
                      value={password} 
                      onChange={(e) => setPassword(e.target.value)} 
                      required
                      sx={{ 
                        mb: 3,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          bgcolor: colors.inputBg,
                          '&:hover fieldset': { borderColor: colors.primary },
                          '&.Mui-focused fieldset': { borderColor: colors.primary, borderWidth: 2 }
                        },
                        '& .MuiInputLabel-root.Mui-focused': { color: colors.primary }
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Lock sx={{ color: colors.textSecondary }} />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowPassword(!showPassword)}
                              edge="end"
                              size="small"
                              sx={{ color: colors.textSecondary }}
                            >
                              {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />

                    {/* Login Button */}
                    <Button 
                      type="submit" 
                      variant="contained" 
                      fullWidth 
                      size="large" 
                      disabled={loading}
                      sx={{ 
                        py: 1.75, 
                        borderRadius: 2,
                        fontSize: '1rem',
                        fontWeight: 'bold',
                        textTransform: 'none',
                        background: isDark 
                          ? 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)'
                          : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        boxShadow: isDark 
                          ? '0 8px 25px rgba(30, 60, 114, 0.4)'
                          : '0 8px 25px rgba(102, 126, 234, 0.4)',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: isDark 
                            ? '0 12px 35px rgba(30, 60, 114, 0.5)'
                            : '0 12px 35px rgba(102, 126, 234, 0.5)',
                        },
                        '&:active': {
                          transform: 'translateY(0)',
                        },
                        transition: 'all 0.3s ease',
                        '&.Mui-disabled': {
                          background: isDark ? '#2d2d44' : '#e0e0e0',
                        }
                      }}
                      startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <LoginIcon />}
                    >
                      {loading ? t.loggingIn : t.login}
                    </Button>
                  </form>

                  {/* Feature info strip */}
                  <Box sx={{ mt: 3 }}>
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 1.5,
                    }}>
                      <Box sx={{ flex: 1, height: '1px', bgcolor: isDark ? alpha('#fff', 0.1) : alpha(colors.primary, 0.15) }} />
                      <Typography sx={{ fontSize: '0.62rem', color: colors.textSecondary, px: 0.5, whiteSpace: 'nowrap' }}>
                        {isRTL ? 'نظام محاسبة متكامل' : 'All-in-one Platform'}
                      </Typography>
                      <Box sx={{ flex: 1, height: '1px', bgcolor: isDark ? alpha('#fff', 0.1) : alpha(colors.primary, 0.15) }} />
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'space-around' }}>
                      {[
                        { icon: <Security sx={{ fontSize: 16 }} />, label: t.feature2, color: '#4CAF50' },
                        { icon: <Speed sx={{ fontSize: 16 }} />,    label: t.feature3, color: '#2196F3' },
                        { icon: <TrendingUp sx={{ fontSize: 16 }} />, label: t.feature1, color: '#9C27B0' },
                      ].map((f, i) => (
                        <Box key={i} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.4 }}>
                          <Box sx={{ color: f.color, opacity: isDark ? 0.75 : 0.85, display: 'flex' }}>
                            {f.icon}
                          </Box>
                          <Typography sx={{ fontSize: '0.6rem', color: colors.textSecondary, textAlign: 'center' }}>
                            {f.label}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>

                </Box>
              </Box>

              {/* Bottom decorative text */}
              <Typography 
                variant="caption" 
                sx={{ 
                  display: 'block',
                  textAlign: 'center', 
                  mt: 3, 
                  color: 'rgba(255,255,255,0.5)',
                  fontSize: '0.75rem'
                }}
              >
                {t.copyright}
              </Typography>
            </Box>
          </Zoom>
        </Box>
      </Container>
    </Box>
  );
};

export default LoginPage;